// packages/web-ui/src/components/onboarding/Step4Tour.tsx
//
// W2-S10 Task 1.5 (AC #1) — Step 4: 4-spot guided tour. Lists each spot in
// the AppShell and, on each entry, briefly describes what the user can do
// there. The tour is a static list rendered inside the takeover (no
// out-of-modal spotlight rays — keeping scope narrow and AppShell-untouched
// per the parallel-lane brief).
//
// A future polish pass can replace the static cards with a spotlight overlay
// that pops the takeover and highlights the in-page region. For now this
// satisfies AC #1 ("4 steps … Step 4 Tour") with no AppShell coupling.

import type { JSX } from 'react';

interface TourSpot {
  id: 'activity-bar' | 'context-panel' | 'composer' | 'utility-drawer';
  title: string;
  description: string;
}

const TOUR_SPOTS: ReadonlyArray<TourSpot> = [
  {
    id: 'activity-bar',
    title: 'Activity bar',
    description:
      'The vertical strip on the left switches between Chat, KB, Files, Views, and Settings.',
  },
  {
    id: 'context-panel',
    title: 'Context panel',
    description:
      'Sidebar showing the relevant context for the active destination — chat sessions, KB tree, recent files, etc.',
  },
  {
    id: 'composer',
    title: 'Composer',
    description:
      'Type messages to TESSA at the bottom of any chat tab. Use @ for mentions and / for slash commands.',
  },
  {
    id: 'utility-drawer',
    title: 'Utility drawer',
    description:
      'Right-side panel with Activity / Detail / Debug tabs — surfaces tool-call traces and per-entity details.',
  },
];

export interface Step4TourProps {
  testId?: string;
}

export function Step4Tour({ testId = 'onboarding-step4' }: Step4TourProps): JSX.Element {
  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-taupe-900">Quick tour</h2>
      <p className="text-sm text-taupe-700">
        TESSA's AppShell has four key regions. Click <em>Done</em> below to start using
        them.
      </p>

      <ol role="list" className="flex flex-col gap-2">
        {TOUR_SPOTS.map((spot, idx) => (
          <li
            key={spot.id}
            data-testid={`${testId}-spot-${spot.id}`}
            className="flex items-start gap-3 rounded border border-taupe-300 bg-cream-50 p-3"
          >
            <span
              aria-hidden="true"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-taupe-300 text-xs font-semibold text-taupe-900"
            >
              {idx + 1}
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-taupe-900">{spot.title}</span>
              <span className="text-xs text-taupe-700">{spot.description}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Exported for test assertions. */
export const TOUR_SPOT_IDS = TOUR_SPOTS.map((s) => s.id);
